import type { DbExecutor } from "./executor";
import { AssemblyRepository, type ComponentEditInput } from "./repositories/assemblyRepository";
import type { AssemblyInstanceComponent } from "../domain/assembly";
import { ValidationError } from "./repositories/projectRepository";
import {
  COUNTERTOP_HEIGHT_IN,
  DEFAULT_COUNTERTOP,
  countertopFigures,
  materialById,
  overhangPu,
  validateCountertop,
  type CountertopFigures,
  type CountertopSpec,
} from "../domain/countertop";
import { cabinetClassOf } from "../domain/objectConfig";
import {
  countertopRunFor,
  createObject,
  objectRunLength,
  resizeObject,
  runMatches,
  type PlanModel,
  type PlanObject,
} from "../geometry";
import type { ScopeSession } from "../design/scopeSession";

/**
 * COUNTERTOPS, END TO END (spec §63).
 *
 * What it is made of, how big it actually is once the overhang is counted,
 * what has to be cut out of it — and how those turn into an estimate through
 * the assembly engine that already exists. There is no second pricing path
 * here: this service sets quantities and inclusions on the ordinary
 * countertop assembly, and the engine does the arithmetic.
 */

export interface CountertopRecord extends CountertopSpec {
  objectId: string;
  /** The cabinets it was measured from, when it was measured from any. */
  cabinetIds: string[];
}

interface Row {
  object_id: string;
  material: string;
  material_note: string;
  thickness_in_x10: number | null;
  overhang_in_x10: number;
  seating_overhang_in_x10: number | null;
  layout: string;
  backsplash: string;
  edge_profile: string;
  waterfall_ends: number;
  sink_cutouts: number;
  cooktop_cutouts: number;
}

/** Which cabinets can carry a countertop: the ones you stand things on. */
export function carriesCountertop(definitionId: string): boolean {
  const cabinetClass = cabinetClassOf(definitionId);
  return cabinetClass === "base" || cabinetClass === "vanity";
}

export class CountertopService {
  private readonly assemblies: AssemblyRepository;

  constructor(private readonly db: DbExecutor) {
    this.assemblies = new AssemblyRepository(db);
  }

  private map(row: Row, cabinetIds: string[]): CountertopRecord {
    return {
      objectId: row.object_id,
      material: (row.material as CountertopSpec["material"]) ?? DEFAULT_COUNTERTOP.material,
      materialNote: row.material_note,
      thicknessInX10: row.thickness_in_x10 === null ? null : Number(row.thickness_in_x10),
      overhangInX10: Number(row.overhang_in_x10),
      seatingOverhangInX10: row.seating_overhang_in_x10 === null ? null : Number(row.seating_overhang_in_x10),
      layout: (row.layout as CountertopSpec["layout"]) ?? "run",
      backsplash: (row.backsplash as CountertopSpec["backsplash"]) ?? "standard",
      edge: (row.edge_profile as CountertopSpec["edge"]) ?? "standard",
      waterfallEnds: Number(row.waterfall_ends),
      sinkCutouts: Number(row.sink_cutouts),
      cooktopCutouts: Number(row.cooktop_cutouts),
      cabinetIds,
    };
  }

  async get(objectId: string): Promise<CountertopRecord | null> {
    const rows = await this.db.select<Row>("SELECT * FROM plan_countertops WHERE object_id = ?", [objectId]);
    if (rows.length === 0) return null;
    const cabinets = await this.db.select<{ cabinet_id: string }>(
      "SELECT cabinet_id FROM plan_countertop_cabinets WHERE countertop_id = ? ORDER BY created_at",
      [objectId],
    );
    return this.map(rows[0], cabinets.map((c) => c.cabinet_id));
  }

  /** The record, creating the default one the first time it is asked for. */
  async ensure(objectId: string): Promise<CountertopRecord> {
    const existing = await this.get(objectId);
    if (existing) return existing;
    const now = new Date().toISOString();
    await this.db.execute(
      `INSERT OR IGNORE INTO plan_countertops
         (object_id, material, material_note, thickness_in_x10, overhang_in_x10, seating_overhang_in_x10,
          layout, backsplash, edge_profile, waterfall_ends, sink_cutouts, cooktop_cutouts, created_at, updated_at)
       VALUES (?, ?, '', NULL, ?, NULL, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        objectId,
        DEFAULT_COUNTERTOP.material,
        DEFAULT_COUNTERTOP.overhangInX10,
        DEFAULT_COUNTERTOP.layout,
        DEFAULT_COUNTERTOP.backsplash,
        DEFAULT_COUNTERTOP.edge,
        DEFAULT_COUNTERTOP.waterfallEnds,
        DEFAULT_COUNTERTOP.sinkCutouts,
        DEFAULT_COUNTERTOP.cooktopCutouts,
        now,
        now,
      ],
    );
    return (await this.get(objectId))!;
  }

  async save(objectId: string, spec: CountertopSpec): Promise<void> {
    const problems = validateCountertop(spec);
    if (problems.length > 0) throw new ValidationError(problems);
    await this.ensure(objectId);
    await this.db.execute(
      `UPDATE plan_countertops SET material = ?, material_note = ?, thickness_in_x10 = ?, overhang_in_x10 = ?,
          seating_overhang_in_x10 = ?, layout = ?, backsplash = ?, edge_profile = ?, waterfall_ends = ?,
          sink_cutouts = ?, cooktop_cutouts = ?, updated_at = ? WHERE object_id = ?`,
      [
        spec.material,
        spec.materialNote,
        spec.thicknessInX10,
        spec.overhangInX10,
        spec.seatingOverhangInX10,
        spec.layout,
        spec.backsplash,
        spec.edge,
        spec.waterfallEnds,
        spec.sinkCutouts,
        spec.cooktopCutouts,
        new Date().toISOString(),
        objectId,
      ],
    );
  }

  /**
   * The countertop measured over this cabinet, if there is one. A cabinet
   * sits ON TOP of its countertop on the plan, so clicking the drawing
   * finds the cabinet — this is how the panel offers the top itself.
   */
  async countertopOverCabinet(cabinetId: string): Promise<string | null> {
    const rows = await this.db.select<{ countertop_id: string }>(
      `SELECT c.countertop_id FROM plan_countertop_cabinets c
         JOIN plan_objects o ON o.id = c.countertop_id
        WHERE c.cabinet_id = ? AND o.archived_at IS NULL LIMIT 1`,
      [cabinetId],
    );
    return rows[0]?.countertop_id ?? null;
  }

  /* ---------------- geometry ---------------- */

  /**
   * Create a countertop over these cabinets, measured from them (§63C).
   * Refuses when they are not one continuous surface, rather than inventing
   * a slab that spans a gap.
   */
  async createOverCabinets(
    session: ScopeSession,
    cabinetIds: readonly string[],
  ): Promise<{ objectId: string } | { problem: string }> {
    const model = session.snapshot().model;
    const cabinets = cabinetIds
      .map((id) => model.objects.get(id))
      .filter((o): o is PlanObject => !!o && o.archivedAt === null);
    if (cabinets.length === 0) return { problem: "Those cabinets are no longer in this plan." };
    const run = countertopRunFor(model, cabinets);
    if (!run.ok) return { problem: run.problem };

    const spec = DEFAULT_COUNTERTOP;
    const depth = run.run.cabinetDepthPu + overhangPu(spec, "front");
    const result = session.commit("Add countertop", (m, ctx) =>
      createObject(m, ctx, {
        definitionId: "obj-countertop",
        geometryType: "area",
        at: run.run.start,
        end: run.run.end,
        width: 0,
        depth,
        heightInches: COUNTERTOP_HEIGHT_IN,
        mountHeightInches: COUNTERTOP_HEIGHT_IN,
      }),
    );
    if (!result) return { problem: "That countertop could not be drawn." };
    await session.flush();

    await this.ensure(result.objectId);
    const now = new Date().toISOString();
    for (const cabinetId of run.run.cabinetIds) {
      await this.db.execute(
        "INSERT OR IGNORE INTO plan_countertop_cabinets (countertop_id, cabinet_id, created_at) VALUES (?, ?, ?)",
        [result.objectId, cabinetId, now],
      );
    }
    return { objectId: result.objectId };
  }

  /**
   * The cabinets moved. Follow them — unless the user has taken the top over
   * by hand, in which case say what changed and leave their work alone
   * (§63D: automation helps, it does not overwrite).
   */
  async refreshFromCabinets(
    session: ScopeSession,
    countertopId: string,
  ): Promise<{ updated: boolean; problem?: string }> {
    const record = await this.get(countertopId);
    if (!record || record.cabinetIds.length === 0) return { updated: false };
    const model: PlanModel = session.snapshot().model;
    const countertop = model.objects.get(countertopId);
    if (!countertop || countertop.archivedAt !== null) return { updated: false };
    if (countertop.detached) {
      return { updated: false, problem: "This countertop was edited by hand, so it no longer follows the cabinets." };
    }

    const cabinets = record.cabinetIds
      .map((id) => model.objects.get(id))
      .filter((o): o is PlanObject => !!o && o.archivedAt === null);
    if (cabinets.length === 0) {
      return { updated: false, problem: "The cabinets this countertop was measured from are gone." };
    }
    const run = countertopRunFor(model, cabinets);
    if (!run.ok) return { updated: false, problem: run.problem };

    const overhang = overhangPu(record, "front");
    if (runMatches(run.run, countertop, overhang)) return { updated: false };

    session.commit("Countertop follows the cabinets", (m, ctx) => {
      const object = m.objects.get(countertopId)!;
      const next = {
        ...object,
        x: run.run.start.x,
        y: run.run.start.y,
        endX: run.run.end.x,
        endY: run.run.end.y,
        depth: run.run.cabinetDepthPu + overhang,
      };
      void ctx;
      return {
        model: { ...m, objects: new Map(m.objects).set(countertopId, next) },
        changes: [{ kind: "object", id: countertopId, before: object, after: next }],
      };
    });
    await session.flush();
    return { updated: true };
  }

  /** Set the overhang, which changes the top's depth on the plan. */
  async setOverhang(session: ScopeSession, countertopId: string, overhangInX10: number): Promise<void> {
    const record = await this.ensure(countertopId);
    await this.save(countertopId, { ...record, overhangInX10 });
    const model = session.snapshot().model;
    const countertop = model.objects.get(countertopId);
    if (!countertop) return;
    const cabinets = record.cabinetIds
      .map((id) => model.objects.get(id))
      .filter((o): o is PlanObject => !!o && o.archivedAt === null);
    const baseDepth =
      cabinets.length > 0
        ? Math.max(...cabinets.map((c) => c.depth))
        : countertop.depth - overhangPu(record, "front");
    session.commit("Countertop overhang", (m, ctx) =>
      resizeObject(m, ctx, countertopId, {
        depth: baseDepth + overhangPu({ ...record, overhangInX10 }, "front"),
      }),
    );
    await session.flush();
  }

  /* ---------------- figures & estimate ---------------- */

  figuresFor(object: PlanObject, spec: CountertopSpec): CountertopFigures {
    return countertopFigures({
      lengthPu: objectRunLength(object),
      depthPu: object.depth,
      spec,
      heightInches: object.mountHeightInches ?? object.heightInches ?? COUNTERTOP_HEIGHT_IN,
    });
  }

  /**
   * Push the countertop's real figures into its assembly (spec §63F).
   *
   * Every number written here is a quantity or an inclusion on the ORDINARY
   * countertop assembly — cutouts counted, waterfall area, splash length,
   * the material's own per-foot assumption. The assembly engine prices it;
   * this never adds a second way to calculate money.
   *
   * A FABRICATOR QUOTE is left completely alone: a quote is a number
   * somebody gave, and rewriting its parts would turn it into a calculation
   * wearing a quote's name.
   */
  async applyToEstimate(featureId: string, object: PlanObject, spec: CountertopSpec): Promise<void> {
    const figures = this.figuresFor(object, spec);
    const material = materialById(spec.material);
    const instances = await this.assemblies.listByFeature(featureId);

    for (const instance of instances) {
      if (instance.templateId === "asm-countertop-quote") continue;
      if (instance.templateId !== "asm-replace-countertops") continue;

      for (const component of instance.components) {
        if (component.archivedAt !== null) continue;
        const name = component.name.toLowerCase();

        if (name.includes("countertop material") && material.costPerSqftCents > 0) {
          if (component.unitCostCents !== material.costPerSqftCents) {
            await this.assemblies.updateComponent(instance.id, component.id, {
              ...toEdit(component),
              unitCostCents: material.costPerSqftCents,
              notes: `${material.label} — Berrier Reno's illustrative rate per square foot. Change it to your own.`,
            });
          }
          continue;
        }

        if (name.includes("sink cutout")) {
          await this.setCount(instance.id, component.id, spec.sinkCutouts);
          continue;
        }
        if (name.includes("cooktop cutout")) {
          await this.setCount(instance.id, component.id, spec.cooktopCutouts);
          continue;
        }
        if (name.includes("standard backsplash")) {
          // A full-height splash is wall tile and is NOT priced here (§63E).
          const include = spec.backsplash === "standard" && figures.backsplashLfX100 > 0;
          await this.assemblies.setComponentIncluded(instance.id, component.id, include);
          if (include) await this.assemblies.setQtyOverride(instance.id, component.id, figures.backsplashLfX100);
          continue;
        }
        if (name.includes("waterfall")) {
          const include = spec.waterfallEnds > 0 && figures.waterfallAreaSqftX100 > 0;
          await this.assemblies.setComponentIncluded(instance.id, component.id, include);
          if (include) {
            await this.assemblies.setQtyOverride(instance.id, component.id, figures.waterfallAreaSqftX100);
          }
          continue;
        }
        if (name.includes("upgraded edge")) {
          const include = spec.edge === "upgraded" && figures.frontEdgeLfX100 > 0;
          await this.assemblies.setComponentIncluded(instance.id, component.id, include);
          if (include) await this.assemblies.setQtyOverride(instance.id, component.id, figures.frontEdgeLfX100);
        }
      }
    }
  }

  private async setCount(instanceId: string, componentId: string, count: number): Promise<void> {
    const include = count > 0;
    await this.assemblies.setComponentIncluded(instanceId, componentId, include);
    if (include) await this.assemblies.setQtyOverride(instanceId, componentId, count * 100);
  }
}

/** An existing component, as the shape updateComponent expects back. */
function toEdit(component: AssemblyInstanceComponent): ComponentEditInput {
  return {
    name: component.name,
    componentType: component.componentType,
    role: component.role,
    inclusion: component.inclusion,
    conditionOptionId: component.conditionOptionId,
    qtyBasis: component.qtyBasis,
    fixedQtyX100: component.fixedQtyX100,
    multiplierX10000: component.multiplierX10000,
    wastePctX100: component.wastePctX100,
    unit: component.unit,
    unitCostCents: component.unitCostCents,
    laborMinutesX100: component.laborMinutesX100,
    taxable: component.taxable,
    categoryId: component.categoryId,
    materialDefinitionId: component.materialDefinitionId ?? null,
    notes: component.notes,
  };
}

